import { ComponentFactoryResolver, ComponentRef, Directive, ElementRef, EventEmitter, Input, Output, Renderer2, ViewContainerRef } from '@angular/core';
import { WorkflowElementType } from '../components/designer/models/Enums/workflow-element-type';

/**
 * Permet d'afficher un composant sous forme de tooltip au survol par la souris.
 */
@Directive({
    selector: '[hubComponentTooltip]',
    standalone: true,
})
export class ComponentToolTipDirective {
    /**
     * Composant utilisé pour afficher la tooltip
     */
    @Input('hubComponentTooltip') component: any;

    /**
     * Position de la tooltip.
     */
    @Input()
    tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

    /**
     * Données de la tooltip.
     */
    @Input()
    tooltipData: any;

    /**
     * Evenements remontés par la tooltip
     */
    @Output()
    tooltipEvent = new EventEmitter<{ sourceType: WorkflowElementType; sourceId: number }>();

    /**
     * Element Html contenant la tooltip.
     */
    private popupComponentRef: ComponentRef<any> | null = null;

    /**
     * Valeur indiquant si la tooltip est affichée.
     */
    private tooltipVisible: boolean = false;

    /**
     * Une reference à l'instruction setTimeout utilisé pour fermer la tooltip.
     */
    private hideTimeoutRef: any = null;

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private viewContainerRef: ViewContainerRef,
        private componentFactoryResolver: ComponentFactoryResolver
    ) {
        this.el.nativeElement.addEventListener('mouseenter', this.createTooltip.bind(this));
        this.el.nativeElement.addEventListener('mouseleave', this.hidePopup.bind(this));
    }

    /**
     * Crée la tooltip.
     */
    private createTooltip() {
        if (this.component && !this.popupComponentRef) {
            console.log('create tooltip');
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(this.component);
            this.popupComponentRef = this.viewContainerRef.createComponent(componentFactory);

            if (this.popupComponentRef) {
                this.popupComponentRef.setInput('data', this.tooltipData);
                this.popupComponentRef.instance.validityElementClick.subscribe((val) => {
                    console.log('Front component tooltip', val);
                    this.tooltipEvent.emit(val);
                });

                const hostElem = this.el.nativeElement;
                const popupElem = this.popupComponentRef.location.nativeElement;

                this.renderer.listen(popupElem, 'mouseenter', () => {
                    this.keepTooltipVisible(); // Garder le tooltip visible
                });

                this.renderer.listen(popupElem, 'mouseleave', () => {
                    this.hidePopup();
                });

                /* Gestion de l'emplacement où est affiché la tooltip */

                const tooltipHeight = popupElem.offsetHeight;
                const tooltipWidth = popupElem.offsetWidth;

                let tooltipTop: number;
                let tooltipLeft: number;

                const { left, top, width, height } = hostElem.getBoundingClientRect();

                // Positionnement selon la position spécifiée
                switch (this.tooltipPosition) {
                    case 'top':
                        tooltipTop = top - tooltipHeight - 5; // Positionner au-dessus
                        tooltipLeft = left + width / 2 - tooltipWidth / 2;
                        break;
                    case 'bottom':
                        tooltipTop = top + height + 5; // Positionner en dessous
                        tooltipLeft = left + width / 2 - tooltipWidth / 2;
                        break;
                    case 'left':
                        tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                        tooltipLeft = left - tooltipWidth - 5; // Positionner à gauche
                        break;
                    case 'right':
                        tooltipTop = top + height / 2 - tooltipHeight / 2; // Centrer verticalement
                        tooltipLeft = left + width + 5; // Positionner à droite
                        break;
                    default:
                        tooltipTop = top - tooltipHeight - 5; // Fallback par défaut
                        tooltipLeft = left + width / 2 - tooltipWidth / 2;
                }

                // Vérifier les débordements
                const windowHeight = window.innerHeight;
                const windowWidth = window.innerWidth;

                // Ajustements pour éviter les débordements
                if (tooltipTop < 0) {
                    tooltipTop = top + height + 5; // Revenir en dessous si débordement
                }
                if (tooltipTop + tooltipHeight > windowHeight) {
                    tooltipTop = top - tooltipHeight - 5; // Revenir au-dessus si débordement
                }
                if (tooltipLeft < 0) {
                    tooltipLeft = 5; // Assurer un espacement
                }
                if (tooltipLeft + tooltipWidth > windowWidth) {
                    tooltipLeft = windowWidth - tooltipWidth - 5; // Ajuster à gauche
                }
                popupElem.style.position = 'absolute';
                popupElem.style.left = `${tooltipLeft}px`;
                popupElem.style.top = `${tooltipTop}px`;
            }
        }
    }

    /**
     * Garde la tooltip visible.
     */
    private keepTooltipVisible() {
        if (this.hideTimeoutRef) {
            // Annule la fermeture de la tooltip.
            clearTimeout(this.hideTimeoutRef);
        }
    }

    /**
     * Masque la tooltip.
     */
    private hidePopup() {
        if (this.popupComponentRef) {
            console.log('ask hidePopup');
            this.hideTimeoutRef = setTimeout(() => {
                console.log('hide Popup ok');
                this.popupComponentRef.destroy();
                this.popupComponentRef = null;
                this.tooltipVisible = false; // Réinitialiser l'état de visibilité
            }, 300); // Correspond à la durée de la transition
        }
    }

    /**
     * Appellée lorsque la directive est détruite.
     */
    ngOnDestroy() {
        console.log('destroy');
        this.hidePopup();
        this.el.nativeElement.removeEventListener('mouseenter', this.createTooltip.bind(this));
        this.el.nativeElement.removeEventListener('mouseleave', this.hidePopup.bind(this));
    }
}
